import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import { categories } from '@/mocks/data';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黄瓜视频网页版',
    url: 'https://www.huanggua-video.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '黄瓜视频网页版',
      description: '黄瓜视频网页版是一款无需下载安装、直接在浏览器打开即可使用的在线视频平台。',
      url: 'https://www.huanggua-video.com',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@huanggua-video.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.huanggua-video.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜视频网页版需要下载安装吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '完全不需要！黄瓜视频网页版是一款纯在线视频平台，直接在浏览器中打开即可使用，无需下载任何软件或插件。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜视频网页版是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，完全免费！黄瓜视频网页版致力于为用户提供免费、优质的在线视频观看体验。所有视频内容均可免费观看。',
        },
      },
      {
        '@type': 'Question',
        name: '观看视频需要注册账号吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！黄瓜视频网页版坚持免费开放的原则，用户无需注册即可直接观看所有视频内容。',
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们 - 黄瓜视频网页版"
        description="黄瓜视频网页版致力于为用户提供免费、优质的在线视频观看体验。了解我们的品牌故事、口碑评价和联系方式。"
        keywords="黄瓜视频网页版,关于黄瓜视频,在线视频平台,免费视频网站"
        canonicalUrl="https://www.huanggua-video.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <main>
        <section className="pt-24 md:pt-28 pb-6 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line mx-2"></i>
                <span className="text-foreground-700 font-medium">关于我们</span>
              </nav>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
                  关于<span className="text-primary-600">黄瓜视频</span>
                </h1>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>黄瓜视频网页版</strong>成立于2024年，是一款专注于提供<strong>免费在线视频观看</strong>服务的创新平台。我们的核心理念是让每一位用户都能以最简单、最便捷的方式享受高质量的视频内容——无需下载安装任何软件，无需注册账号，只需打开浏览器即可畅享海量视频资源。
                  </p>
                  <p>
                    平台覆盖<strong>电影</strong>、<strong>电视剧</strong>、<strong>综艺</strong>、<strong>动漫</strong>、<strong>纪录片</strong>、<strong>体育赛事</strong>等六大核心品类，每日更新精彩内容，满足不同用户的观影偏好。无论是热门院线大片、经典剧集重温，还是最新综艺节目，黄瓜视频网页版都能让您轻松找到心仪的内容。
                  </p>
                  <p>
                    技术上，我们采用先进的视频编码和CDN加速方案，支持从流畅到4K超高清的多档画质选择，确保在不同网络环境下都能获得<strong>流畅的观影体验</strong>。同时，页面自适应PC端、移动端和平板设备，让您随时随地，想怎么看就怎么看。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20multiple%20screens%20displaying%20video%20content%2C%20warm%20ambient%20lighting%2C%20collaborative%20team%20environment%2C%20minimalist%20design%2C%20technology%20company%20vibe%2C%20comfortable%20and%20inviting%20atmosphere%2C%20natural%20light%20from%20windows&width=900&height=675&seq=about-hero-01&orientation=landscape"
                  alt="黄瓜视频团队工作环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                我们的<span className="text-primary-600">优势</span>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">为什么千万用户选择黄瓜视频网页版</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { icon: 'ri-download-cloud-2-line', title: '无需下载', desc: '纯网页端播放，打开浏览器即可观看，不占用手机或电脑存储空间' },
                { icon: 'ri-vip-crown-line', title: '完全免费', desc: '所有视频内容均可免费观看，无隐藏收费项目，无强制广告干扰' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美兼容PC、手机、平板和智能电视，不同设备自动适配最佳界面' },
                { icon: 'ri-speed-up-line', title: '极速播放', desc: 'CDN加速技术确保全球范围流畅播放，自动选择最优画质方案' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                用户<span className="text-primary-600">口碑</span>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">听听真实用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '张先生',
                  role: '影视爱好者',
                  rating: 5,
                  text: '以前总要在各个APP之间切换找剧看，自从发现了黄瓜视频网页版，一个网站就搞定了。画质清晰，加载快，关键是真心免费！已经推荐给了身边所有朋友。',
                },
                {
                  name: '李女士',
                  role: '全职妈妈',
                  rating: 5,
                  text: '孩子喜欢看动漫，我追剧。黄瓜视频的分类很清晰，操作也简单，连我家小朋友都能自己找到想看的动漫。不需要下载APP太方便了，手机内存终于够用了。',
                },
                {
                  name: '王同学',
                  role: '大学生',
                  rating: 5,
                  text: '学习和娱乐两不误！纪录片板块质量很高，老师推荐的几部自然类纪录片这里都有。晚上宿舍熄灯后还能用手机继续看，画质自动适配，体验超棒。',
                },
                {
                  name: '赵先生',
                  role: 'IT工程师',
                  rating: 4,
                  text: '作为一个码农，我特别在意平台的流畅度。黄瓜视频的技术做得确实不错，播放基本没有卡顿，视频加载速度也很快。用电脑大屏看4K电影，体验不输专业播放器。',
                },
                {
                  name: '孙女士',
                  role: '退休教师',
                  rating: 5,
                  text: '年纪大了不太会用复杂的软件，黄瓜视频网页版真的很友好，打开浏览器就能看，不需要注册登录。纪录片内容很丰富，每天和老伴一起看，生活充实了很多。',
                },
                {
                  name: '刘先生',
                  role: '运动达人',
                  rating: 5,
                  text: '体育赛事回放太全了！NBA经典赛、世界杯名场面都能找到。下班回家瘫在沙发上用电视浏览器打开，大屏看球赛，爽！最主要是免费的，比那些收费平台良心多了。',
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-xs md:text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-600 text-sm"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  联系<span className="text-primary-600">我们</span>
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们的客服团队将尽快为您提供帮助。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</div>
                      <div className="text-sm text-foreground-500">contact@huanggua-video.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-customer-service-2-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">在线客服</div>
                      <div className="text-sm text-foreground-500">工作日 9:00 - 21:00，周末 10:00 - 18:00</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-question-answer-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">常见问题</div>
                      <div className="text-sm text-foreground-500">
                        访问{' '}
                        <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                          首页FAQ区域
                        </Link>
                        {' '}查看常见问题解答
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  快速<span className="text-primary-600">导航</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-200 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-8 p-5 rounded-xl bg-accent-100/60 border border-accent-200/60">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 flex-shrink-0">
                      <i className="ri-lightbulb-line text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">合作与建议</div>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        我们欢迎内容创作者、版权方及各领域合作伙伴与我们联系。如果您有任何合作意向或改进建议，请发送邮件至 contact@huanggua-video.com。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-primary-500">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              加入千万用户的大家庭
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 leading-relaxed">
              无需下载，无需注册，打开浏览器就能享受海量免费视频内容。现在就开始你的观影之旅吧！
            </p>
            <Link
              to="/category/dianying"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              立即开始观看
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}